import { get, post, postCustomUrl } from './client';
import type { Coordinates, DateRangeStr, HourRange } from '../types/types';
import { currentUrlWithExtraQueryParams, removeHashFromUrl } from '../utils/utils';
import type { SearchParams } from '../redux/types';
import type { WidgetConfig } from '../types/config';

export interface PaymentSize {
  value: number;
  type: string;
}

const ajaxRequests: {[id: string]: AbortController[]} = {}

const abortAjaxRequests = (id) => {
  if (!ajaxRequests[id]) {
    return
  }

  ajaxRequests[id].forEach((controller) => {
    controller.abort()
  })

  ajaxRequests[id] = []
}

const addAjaxRequest = (id: string, abortController: AbortController) => {
  if (!ajaxRequests[id]) {
    ajaxRequests[id] = []
  }

  ajaxRequests[id].push(abortController)
}

export const fetchShippingStepOptions = (orderReference: string, onSuccess: (items: any) => any, onError: () => any, coordinates?: Coordinates, address?: string) => {
  const params = coordinates ? { ...coordinates, address: address } : undefined;
  post(`/booking/order/${orderReference}/shipping-step`, params)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postShippingMethod = (orderReference: string, optionId: any, grossPrice: number, address: string, onSuccess: () => any, onError: () => any) => {
  post(`/booking/order/${orderReference}/shipping`, {
    method: { id: optionId },
    price: grossPrice,
    address: address,
  })
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError()) {
        onError();
      }
    });
};

export const postCreateOrder = (payload, onSuccess: (data: any) => any, onError: (err: any) => any) => {
  post('/booking/order/create', payload)
    .then(({ data }) => {
      if (onSuccess) {
        onSuccess(data);
      }
    })
    .catch(err => {
      if (onError) {
        onError(err);
      }
    });
};

export const postCreateSaleOrder = (payload, onSuccess: (data: any) => any, onError: (err: any) => any) => {
  post('/booking/sale-order/create', payload)
    .then(({ data }) => {
      if (onSuccess) {
        onSuccess(data);
      }
    })
    .catch(err => {
      if (onError) {
        onError(err);
      }
    });
};

export const postAddItem = (
  orderReference: string,
  itemId: string,
  stepId: string,
  variationsIds: string[] = [],
  mileageOptionId: string,
  amount: any,
  splitOption: any,
  onSuccess: () => any,
  onError: (err: any) => any,
) => {
  const payload = {
    itemId: itemId,
    stepId: stepId,
    variationsIds: variationsIds,
    orderReference: orderReference,
    qty: parseInt(amount),
    mileage: mileageOptionId,
    splitOption: splitOption,
  };

  post('/booking/order/item/add', payload)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(err => {
      if (onError) {
        onError(err);
      }
    });
};
export const postAddGiftCard = (orderReference: string, stepId: string, value: number, quantity: number, onSuccess: () => any, onError: (err: any) => any) => {
  const payload = {
    stepId: stepId,
    orderReference: orderReference,
    qty: quantity,
    value: value,
  };

  post('/booking/sale-order/item/add-gift-card', payload)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(err => {
      if (onError) {
        onError(err);
      }
    });
};

export const removeItem = (
  orderReference: string,
  orderItemId: string,
  stepId: string,
  quantity: number,
  variationsIds: string[] = [],
  splitOption: any,
  onSuccess: () => any,
  onError: () => any,
) => {
  const payload = {
    orderItemId: orderItemId,
    variationsIds: variationsIds,
    orderReference: orderReference,
    stepId: stepId,
    quantity: quantity,
    splitOption: splitOption,
  };

  post('/booking/order/item/remove', payload)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postSearchItems = (params: SearchParams, onSuccess: (data: any) => any, onError: () => any) => {
  post(`/inventory/items/search-widget`, params)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchCartItems = (orderReference: string, onSuccess: (data: any) => any, onError: () => any) => {
  get(`/booking/order/${orderReference}/items`)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchOrderDetails = (orderReference: string, onSuccess: (data: any) => any, onError: () => any) => {
  get(`/booking/order/${orderReference}/details`)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postPersonalDetails = (orderReference: string, values: any, isShipping: boolean, onSuccess: () => any, onError: () => any) => {
  post('/customer/update', {
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    phone: values.phoneNumber,
    company: values.company,
  })
    .then(resp => {
      const customerId = resp.data.id;
      const url = (isShipping && '/booking/order/shipping/add') || '/booking/order/customer/add';
      post(url, {
        customerId: customerId,
        orderReference: orderReference,
      }).then(() => {
        post('/address/update', {
          customerId: customerId,
          city: values.city,
          country: values.country,
          street: values.street,
          zipCode: values.zipCode,
          company: values.company, // company name is already sent to /customer/update - api needs to be changed/fixed later
          addressTwo: values.addressTwo,
          taxId: values.taxId,
        }).then(() => {
          if (onSuccess && typeof onSuccess === 'function') {
            onSuccess();
          }
        });
      });
    })
    .catch(() => {
      if (onError && typeof onError === 'function') {
        onError();
      }
    });
};

export const fetchSteps = (orderReference: string, onSuccess: (data: any) => any, onError: () => any) => {
  post(`/booking/steps`, { orderReference: orderReference ?? '', isOfferMode: false })
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchSystem = (locale: string, onSuccess: (data: any) => any, onError: () => any) => {
  get(`/booking/system`, { forWidgets: 1, locale })
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchTranslations = (locale: string, onSuccess: (data: any) => any, onError: () => any) => {
  get(`/booking/system`, { forWidgets: 1, locale })
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const getPaymentConfig = (orderReference: string, locale: string, onSuccess: (data: any) => any, onError: () => any) => {
  get(`/booking/${locale}/payment-config`, { orderReference })
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const getWidgetCustomization = (onSuccess: (data: any) => any, onError: () => any) => {
  get(`/booking/widget-customization`)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const getWidgetConfig = (onSuccess: (data: any) => any, onError: () => any) => {
  get(`/booking/widget-config`)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postNewWidgetConfig = (widgetConfig: WidgetConfig | WidgetConfig, saveUrl: string, onSuccess: () => any, onError: () => any) => {
  const payload = {
    ...widgetConfig,
  };

  postCustomUrl(saveUrl, payload)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postWidgetConfig = (widgetConfig: WidgetConfig, saveUrl: string, onSuccess: () => any, onError: () => any) => {
  const payload = {
    ...widgetConfig,
  };

  postCustomUrl(saveUrl, payload)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postVoucher = (orderReference: string, onSuccess: () => any, onError: () => any) => {
  const payload = {
    orderReference: orderReference,
  };

  post('/payment/voucher', payload)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postReserve = (orderReference: string, size: PaymentSize, onSuccess: () => any, onError: () => any) => {
  const payload = {
    orderReference: orderReference,
    size,
  };

  post('/payment/reserve', payload)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postPrepayment = (orderReference: string, size: PaymentSize, onSuccess: () => any, onError: () => any) => {
  const payload = {
    orderReference: orderReference,
    size,
  };

  post('/payment/prepay', payload)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postStripePaymentIntent = (
  orderReference: string,
  isOrder: boolean,
  paymentMethodData: any,
  isRecurring: boolean,
  size: PaymentSize,
  onSuccess: (data: any) => any,
  onError: (errMsg?: string) => any,
) => {
  const payload = {
    orderReference: orderReference,
    size,
    cardType: paymentMethodData.card.brand,
    paymentMethodId: paymentMethodData.id,
    paymentType: isRecurring ? 'recurring' : 'oneTime',
  };

  const urlGroup = isOrder ? '' : '/order-page';

  post(`${urlGroup}/payment/stripe/payment-intent/create`, payload)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError('Api Error');
      }
    });
};

export const postStripeConfirmPaymentIntent = (paymentIntentId: string, onSuccess: () => any, onError: (errMsg?: any) => any) => {
  const payload = {
    paymentIntentId: paymentIntentId,
  };

  post('/payment/stripe/payment-intent/confirm', payload)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(_ => {
      if (onError) {
        onError('Api Error');
      }
    });
};

export const postStripeOtherPaymentIntent = (
  orderReference: string,
  isOrder: boolean,
  method: string,
  isRecurring: boolean,
  size: PaymentSize,
  onSuccess: (data: any) => any,
  onError: () => any,
) => {
  const payload = {
    orderReference: orderReference,
    method: method,
    size: size,
    paymentType: isRecurring ? 'recurring' : 'oneTime',
  };

  const urlGroup = isOrder ? '' : '/order-page';

  post(`${urlGroup}/payment/stripe/other-payment-intent/create`, payload)
    .then(resp => {
      if (resp.data.paymentIntentClientSecret) {
        if (onSuccess) {
          onSuccess(resp.data.paymentIntentClientSecret);
        }
      }
    })
    .catch(_ => {
      if (onError) {
        onError();
      }
    });
};

export const postVerifyPaymentIntent = (paymentIntentId: string, onSuccess: () => any, onError: () => any) => {
  post('/payment/stripe/payment-intent/verify', { paymentIntentId: paymentIntentId })
    .then(() => {
      onSuccess?.();
    })
    .catch(() => {
      onError?.();
    });
};

export const postStripeSepaChargeSource = (orderReference: string, sourceId: string, sourceStatus: string, size: PaymentSize, onSuccess: () => any, onError: () => any) => {
  const payload = {
    orderReference: orderReference,
    sourceId: sourceId,
    sourceStatus: sourceStatus,
    size,
  };

  post('/payment/stripe/sepa/charge-source', payload)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(_ => {
      if (onError) {
        onError();
      }
    });
};

export const postPayPalOrderCreate = (orderReference: string, method: string, size: PaymentSize, isOrder: boolean, onSuccess: (data: any) => any, onError: () => any) => {
  const payload = {
    orderReference,
    paymentMethod: method,
    returnUrl: removeHashFromUrl(
      currentUrlWithExtraQueryParams([
        { name: 'rentware-payment', value: 'paypal-success' },
        { name: 'rentware-order-id', value: orderReference },
      ]),
    ),
    cancelUrl: removeHashFromUrl(
      currentUrlWithExtraQueryParams([
        { name: 'rentware-payment', value: 'paypal-cancel' },
        { name: 'rentware-order-id', value: orderReference },
      ]),
    ),
    size,
  };

  const urlGroup = isOrder ? '' : '/order-page';

  post(`${urlGroup}/payment/paypal/order/create`, payload)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

//works both for order and no order page
export const postPayPalCapture = (orderReference: string, paymentId: string, payerId: string, onSuccess: (data: any) => any, onError: () => any) => {
  const payload = {
    orderReference,
    paymentId,
    payerId,
  };

  post('/payment/paypal/order/capture', payload)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postPayPalPlusCreateResource = (orderReference: string, onSuccess: (data: any) => any, onError: () => any) => {
  const payload = {
    orderReference,
    returnUrl: currentUrlWithExtraQueryParams([
      { name: 'rentware-payment', value: 'paypal-success' },
      { name: 'rentware-order-id', value: orderReference },
    ]),
    cancelUrl: currentUrlWithExtraQueryParams([
      { name: 'rentware-payment', value: 'paypal-cancel' },
      { name: 'rentware-order-id', value: orderReference },
    ]),
  };

  post('/payment/paypal/plus/create-resource', payload)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postPayPalUpdateResource = (orderReference: string, paymentId: string, isOrder: boolean, size: PaymentSize, onSuccess: () => any, onError: () => any) => {
  const payload = {
    orderReference,
    paymentId,
    size,
  };

  const urlGroup = isOrder ? '' : '/order-page';

  post(`${urlGroup}/payment/paypal/plus/update-resource`, payload)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const resetOrder = (orderReference: string, onSuccess: () => any, onError: () => any) => {
  post(`/booking/order/${orderReference}/cancel`)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchItemPriceAsync = async (
  itemId: string,
  orderReference: string,
  variationId: string,
  dateRange: DateRangeStr,
  hourRange: HourRange,
  timeSlot: string,
  quantity: number,
  onSuccess: (price: any) => any,
  onError: () => any,
) => {
  const params = {
    orderReference: orderReference,
    fromDate: dateRange.from,
    toDate: dateRange.to,
    fromHour: hourRange ? hourRange.from : undefined,
    toHour: hourRange ? hourRange.to : undefined,
    timeSlot: timeSlot,
    variationId: variationId,
    quantity: quantity,
  };

  get(`/booking/items/${itemId}/free-slot/price`, params)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchItemPrice = (
  itemId: string,
  orderReference: string,
  variationId: string,
  dateRange: DateRangeStr,
  hourRange: HourRange,
  timeSlot: string,
  quantity: number,
  onSuccess: (price: any) => any,
  onError: () => any,
) => {
  const params = {
    orderReference: orderReference,
    fromDate: dateRange.from,
    toDate: dateRange.to,
    fromHour: hourRange ? hourRange.from : undefined,
    toHour: hourRange ? hourRange.to : undefined,
    timeSlot: timeSlot,
    variationId: variationId,
    quantity: quantity,
  };

  get(`/booking/items/${itemId}/free-slot/price`, params)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchCalendarAvailability = (from: string, to: string, locationId: string, mode: string, pickUp: string, onSuccess: (price: any) => any, onError: () => any) => {
  const params: any = {
    from: from,
    to: to,
    locationId: locationId,
    mode: mode,
  };

  if (!!pickUp) {
    params.pickUp = pickUp;
  }

  get(`/booking/schedule-calendar-availability`, params)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchCalendarAvailabilityHours = (from: string, to: string, locationId: string, onSuccess: (price: any) => any, onError: () => any) => {
  const params: any = {
    fromDate: from,
    locationId: locationId,
  };

  if (!!to) {
    params.toDate = to;
  }

  get(`/booking/schedule-calendar-availability/possible-hours`, params)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchDaysAvailability = (
  itemId: string,
  variationId: string,
  dateRange: DateRangeStr,
  mode: string,
  pickUpDate: string,
  onSuccess: (price: any) => any,
  onError: () => any,
  fromHour?: string,
  locationId?: string,
) => {
  const params: any = {
    mode: mode,
    from: dateRange.from,
    to: dateRange.to,
    variationId: variationId,
    locationId: locationId, // only when article has no location assigned
    nearest: 1,
  };

  const dropOffMode = mode === 'dropOff';
  if (dropOffMode) {
    params.pickUp = pickUpDate;
    params.fromHour = fromHour;
  }

  get(`/booking/items/${itemId}/calendar-availability`, params)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchSlots = (itemId: string, pickUpDate: string, variationId: string, onSuccess: (price: any) => any, onError: () => any) => {
  const params: any = {
    date: pickUpDate,
    variationId: variationId,
    forWidgets: true,
  };

  get(`/booking/items/${itemId}/free-slot/possible-hours-single-day`, params)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchAccountData = (onSuccess: (data: any) => any, onError: () => any) => {
  get('/booking/items')
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchPossibleHours = (
  itemId: string,
  variationId: string,
  pickUpDate: string,
  dropOffDate: string,
  onSuccess: (price: any) => any,
  onError: () => any,
  locationId?: string,
) => {
  const params: any = {
    fromDate: pickUpDate,
    toDate: dropOffDate,
    variationId: variationId,
    locationId: locationId, // only when article has no location assigned
  };

  get(`/booking/items/${itemId}/free-slot/possible-hours`, params)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchExpiration = (orderReference: string, onSuccess: (data: any) => any, onError: () => any) => {
  get(`/booking/order/${orderReference}/expiration`)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const fetchCustomerData = (orderReference: string, onSuccess: (data: any) => any, onError: () => any) => {
  get(`/booking/order/${orderReference}/customer`)
    .then(resp => {
      const customer = resp.data;
      if (customer && customer.id) {
        get(`/customer/${customer.id}/address`).then(resp => {
          const address = resp.data;
          if (onSuccess) {
            onSuccess({ customer, address });
          }
        });
      } else {
        if (onSuccess) {
          onSuccess({ customer, address: {} });
        }
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const getNavigateStep = (to: string, stepId: string, orderReference: string, onSuccess: (items: any) => any, onError: () => any) => {
  get(`/booking/order/${orderReference}/navigate-step`, { to: to, stepId: stepId })
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const getPreviousStepItems = (orderReference: string, onSuccess: (items: any) => any, onError: () => any) => {
  get(`/booking/order/${orderReference}/previous-step`)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const getStepItems = (orderReference: string, step: string, onSuccess: (items: any) => any, onError: () => any) => {
  get(`/booking/order/${orderReference}/step/${step}`)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const getStepValid = (orderReference: string, stepId: string, onSuccess: (items: any) => any, onError: () => any) => {
  get(`/booking/order/${orderReference}/steps/${stepId}/valid`)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postStepVisited = (orderReference: string, stepId: string, context: string, onSuccess?: () => any, onError?: () => any) => {
  post(`/booking/order/${orderReference}/steps/${stepId}/visited?context=${context}`)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postVoucherCode = (orderReference: string, code: string, splitOption: any, onSuccess: () => any, onError: (data: any) => any) => {
  post(`/booking/order/${orderReference}/voucher`, { code: code, splitOption: splitOption })
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(err => {
      if (onError) {
        onError(err);
      }
    });
};

export const deleteVoucherCode = (orderReference: string, code: string, splitOption: any, onSuccess: () => any, onError: () => any) => {
  post(`/booking/order/${orderReference}/voucher-remove`, { code: code, splitOption: splitOption })
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postGiftCardCode = (orderReference: string, code: string, splitOption: any, onSuccess: () => any, onError: () => any) => {
  post(`/booking/order/${orderReference}/gift-card/redeem`, { code: code, splitOption: splitOption })
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const deleteGiftCardCode = (orderReference: string, code: string, onSuccess: () => any, onError: () => any) => {
  post(`/booking/order/${orderReference}/gift-card/remove`, { code: code })
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postPrepaymentCode = (orderReference: string, code: string, onSuccess: () => any, onError: () => any) => {
  post(`/booking/order/${orderReference}/passcode/${code}`)
    .then(() => {
      onSuccess?.();
    })
    .catch(() => {
      onError?.();
    });
};

export const postCustomerNotes = (orderReference: string, message: string, onSuccess: () => any, onError: () => any) => {
  post(`/customer/notes`, { orderReference, customerNotes: message })
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const getTerms = (locale: string, onSuccess: (data: any) => any, onError: () => any) => {
  get(`/payment/${locale}/terms`)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postVariationsForItem = (itemId: string, orderReference: string, locale: string, onSuccess: (data: any) => any, onError: () => any) => {
  post(`/booking/items/${itemId}/variations/${locale}`, { orderReference })
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postChangeMileageOption = (itemId: string, orderReference: string, mileageOptionId: string, onSuccess: () => any, onError: () => any) => {
  post(`/booking/order/${orderReference}/change-mileage-option`, {
    offerReference: itemId,
    mileageOptionId,
  })
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postItemDetails = (
  itemId: string,
  orderReference: string,
  locale: string,
  dateRange: DateRangeStr,
  withTime: boolean,
  onSuccess: (data: any) => any,
  onError: () => any,
) => {
  let params: any = { orderReference: orderReference };

  // send extra parameters, only if no order
  if (!orderReference) {
    params = {
      ...params,
      fromDate: dateRange?.from,
      toDate: dateRange?.to,
      withDate: !!dateRange,
      withTime: withTime,
    };
  }

  post(`/booking/items/${itemId}/${locale}`, params)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const getOrderDetails = (publicHash: string, onSuccess: (data: any) => any, onError: () => any) => {
  get(`/order/${publicHash}`)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const getCanRemoveItem = (itemId: string, orderReference: string, stepId: string, onSuccess: (data: any) => any, onError: () => any) => {
  get(`/booking/order/${orderReference}/items/${itemId}/can-remove`, { stepId })
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const getSuccess = (orderReference: string, onSuccess: (data: any) => any, onError: () => any) => {
  get(`/booking/order/${orderReference}/success`)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postStartPayment = (isOrder: boolean, orderReference: string, onSuccess: () => any, onError: (err?: any) => any) => {
  if (isOrder) {
    post('/payment/start', { orderReference: orderReference })
      .then(() => onSuccess?.())
      .catch(err => onError?.(err.msg));
  } else {
    onSuccess?.();
  }
};

export const postGeoCode = (query: string, orderReference: string, onSuccess: (data: any) => any, onError: () => any) => {
  post('/geocode', { addressQuery: query, orderReference: orderReference })
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postSelectPaymentSplit = (orderReference: string, extraCartItemId: string, splitOption: any, onSuccess: () => any, onError: () => any) => {
  const requestId = `postSelectPaymentSplit-${orderReference}`
  abortAjaxRequests(requestId)

  const controller = new AbortController();
  addAjaxRequest(requestId, controller)

  post(`/booking/order/${orderReference}/select-payment-split?extraCartItemId=${extraCartItemId}`, { splitOption: splitOption }, controller.signal)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};

export const postDataFormInputValues = (
  orderReference: string,
  dataFormId: string,
  dataFormInputValues: FormData | { [key: string]: string },
  onSuccess: () => any,
  onError: (err: any) => any,
) => {
  post(`/booking/order/${orderReference}/data-forms/multi-part/${dataFormId}`, dataFormInputValues)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(err => {
      if (onError) {
        onError(err);
      }
    });
};

export const fetchDataFormInputValues = (orderReference: string, dataFormId: string, onSuccess: (data: any) => any, onError: () => any) => {
  get(`/booking/order/${orderReference}/data-forms/${dataFormId}`)
    .then(resp => {
      if (onSuccess) {
        onSuccess(resp.data);
      }
    })
    .catch(() => {
      if (onError) {
        onError();
      }
    });
};
